package er.prateek.crossly.ui.gameplay

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import er.prateek.crossly.config.AppConfig
import er.prateek.crossly.config.GameplayConfig
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.firebase.RemoteConfigKeys
import er.prateek.crossly.game.crossword.KeyboardMode
import er.prateek.crossly.game.crossword.StarterAmount
import er.prateek.crossly.persistence.ChoiceSource
import er.prateek.crossly.persistence.GameplayPreferences
import er.prateek.crossly.persistence.GameplayPrefsStore
import er.prateek.crossly.persistence.StoredChoice

private const val SETTING_KEYBOARD = "keyboard_mode"
private const val SETTING_STARTERS = "starter_letters"

/**
 * The solving-assist choices, held for the whole session and written straight
 * back to [GameplayPrefsStore] — the same shape as
 * [er.prateek.crossly.ui.theme.ThemeState], so Settings edits one object and the
 * board reads it live.
 *
 * Letter keys and starter letters are stored per [DifficultyTier] so Remote Config can give
 * each level its own default, but a player picks once for every level. Each stored value
 * remembers whether the player picked it or it came from config, which is what lets
 * [syncWithConfig] follow a changed `default` without ever overriding a player's own choice.
 */
class GameplayState(
    initial: GameplayPreferences,
    private val store: GameplayPrefsStore,
    config: GameplayConfig,
    private val analytics: AnalyticsLogger,
) {
    private val keyboardModes = mutableStateMapOf<DifficultyTier, StoredChoice<KeyboardMode>>()
        .apply { putAll(initial.keyboardModes) }
    private val starterAmounts = mutableStateMapOf<DifficultyTier, StoredChoice<StarterAmount>>()
        .apply { putAll(initial.starterAmounts) }
    val showClueGutter: MutableState<Boolean> = mutableStateOf(initial.showClueGutter)

    private var config: GameplayConfig = config

    init {
        syncWithConfig(config)
    }

    /** What a puzzle of [tier] uses. */
    fun keyboardMode(tier: DifficultyTier): KeyboardMode =
        keyboardModes[tier]?.value ?: config.keyboardOptions.defaultFor(tier)

    fun starterAmount(tier: DifficultyTier): StarterAmount =
        starterAmounts[tier]?.value ?: config.starterOptions.defaultFor(tier)

    /** Every value currently in effect across the levels -- more than one until the player picks. */
    fun keyboardModesInEffect(): Set<KeyboardMode> = DifficultyTier.entries.mapTo(mutableSetOf(), ::keyboardMode)

    fun starterAmountsInEffect(): Set<StarterAmount> = DifficultyTier.entries.mapTo(mutableSetOf(), ::starterAmount)

    /**
     * A player's pick in Settings: applied to every level and stored as theirs, so later
     * config defaults leave it alone.
     */
    fun setKeyboardMode(mode: KeyboardMode) {
        val from = keyboardModesInEffect().describe()
        DifficultyTier.entries.forEach { keyboardModes[it] = StoredChoice(mode, ChoiceSource.USER) }
        persist()
        logUserChange(SETTING_KEYBOARD, from, mode.name)
    }

    fun setStarterAmount(amount: StarterAmount) {
        val from = starterAmountsInEffect().describe()
        DifficultyTier.entries.forEach { starterAmounts[it] = StoredChoice(amount, ChoiceSource.USER) }
        persist()
        logUserChange(SETTING_STARTERS, from, amount.name)
    }

    fun setShowClueGutter(show: Boolean) {
        showClueGutter.value = show
        persist()
    }

    /**
     * Brings every tier the player hasn't picked for in line with [newConfig]'s defaults and
     * persists the result, so stored preferences always say what's actually in effect.
     *
     * - Never stored (fresh install, or a tier added later): takes the default silently.
     * - Stored from config and the default has since changed: moves to the new default and
     *   logs [AnalyticsEvents.SETTINGS_CHANGED_BY_CONFIG].
     * - Picked by the player: left alone, even if it's no longer offered -- Settings keeps
     *   offering whatever is in effect regardless of the `show_*` flags.
     */
    fun syncWithConfig(newConfig: GameplayConfig) {
        config = newConfig
        var changed = false
        DifficultyTier.entries.forEach { tier ->
            changed = keyboardModes.follow(tier, newConfig.keyboardOptions.defaultFor(tier), SETTING_KEYBOARD) || changed
            changed = starterAmounts.follow(tier, newConfig.starterOptions.defaultFor(tier), SETTING_STARTERS) || changed
        }
        if (changed) persist()
    }

    private fun <T : Enum<T>> MutableMap<DifficultyTier, StoredChoice<T>>.follow(
        tier: DifficultyTier,
        default: T,
        setting: String,
    ): Boolean {
        val current = this[tier]
        if (current?.source == ChoiceSource.USER || current?.value == default) return false
        this[tier] = StoredChoice(default, ChoiceSource.CONFIG)
        if (current != null) {
            analytics.logEvent(
                AnalyticsEvents.SETTINGS_CHANGED_BY_CONFIG,
                mapOf(
                    "setting" to setting,
                    "tier" to tier.name,
                    "from_value" to current.value.name,
                    "to_value" to default.name,
                ),
            )
        }
        return true
    }

    private fun logUserChange(setting: String, from: String, to: String) {
        analytics.logEvent(
            AnalyticsEvents.SETTINGS_CHANGED,
            mapOf("setting" to setting, "from_value" to from, "to_value" to to),
        )
    }

    // `MIXED` when the levels were on different per-level defaults before this pick.
    private fun Set<Enum<*>>.describe(): String = singleOrNull()?.name ?: "MIXED"

    private fun persist() = store.save(
        GameplayPreferences(
            keyboardModes = keyboardModes.toMap(),
            starterAmounts = starterAmounts.toMap(),
            showClueGutter = showClueGutter.value,
        ),
    )
}

/** The gameplay section of whatever `app_config` is currently activated. */
fun currentGameplayConfig(): GameplayConfig =
    AppConfig.parse(AppContainer.remoteConfig.getString(RemoteConfigKeys.APP_CONFIG)).gameplay

@Composable
fun rememberGameplayState(store: GameplayPrefsStore = AppContainer.gameplayPrefsStore): GameplayState =
    remember {
        GameplayState(store.load(), store, currentGameplayConfig(), AppContainer.analytics)
    }
